import { useState, useEffect, useMemo, useCallback } from 'react';

import {
	useGetOnboardingState,
	useSetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { ONBOARDING_INSIGHTS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { fg } from '@confluence/feature-gating';
import { useShouldExcludeUser } from '@confluence/targeting';
import { useExperimentalSiteUserPersonalization } from '@confluence/targeting/entry-points/useExperimentalSiteUserPersonalization.ts';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

const ONBOARDING_DELAY_MS = 3000;
const MIN_READ_TIME_MINUTES = 3;
const CONFLUENCE_FIRST_ACTIVE_TRAIT = 'confluence_first_active';

export const useAudioBriefingChangeboarding = ({
	contentId,
	onShow,
	skip,
	readTime,
}: {
	contentId: string;
	onShow: () => void;
	skip: boolean;
	readTime?: number | null;
}) => {
	const [showChangeboarding, setShowChangeboarding] = useState(false);

	const validReadTime = readTime && readTime >= MIN_READ_TIME_MINUTES;

	const { cloudId, userId } = useSessionData();
	const isLicensed = useIsLicensed();

	const { data: userPersonalizationData, loading: userTargetingLoading } =
		useExperimentalSiteUserPersonalization({
			variables: { cloudId, userId: userId as string },
			skip: !isLicensed || !userId,
		});

	const attributes = userPersonalizationData?.experimentalSiteUserPersonalization?.attributes;
	const confluenceFirstActive = attributes?.find(
		({ name }: { name: string }) => name === CONFLUENCE_FIRST_ACTIVE_TRAIT,
	)?.value;

	const firstActiveDate = confluenceFirstActive ? new Date(confluenceFirstActive) : null;

	const { shouldExclude: shouldExcludeNewUser } = useShouldExcludeUser({
		checkDate: firstActiveDate,
		daysThreshold: 28,
	});

	const shouldSkip =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		!fg('confluence_audio_briefing_changeboarding') ||
		skip ||
		!validReadTime ||
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		shouldExcludeNewUser ||
		userTargetingLoading;

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState([ONBOARDING_INSIGHTS.AUDIO_BRIEFING], shouldSkip);

	const { setOnboardingState } = useSetOnboardingState();
	const { hasAudioBriefingChangeboardingBeenSeen } = deserializeState(onboardingStateData);

	const shouldShowChangeboarding = useMemo(() => {
		const areConditionsMet =
			!shouldSkip &&
			!onboardingStateLoading &&
			!onboardingStateError &&
			!hasAudioBriefingChangeboardingBeenSeen;

		return !!areConditionsMet;
	}, [
		onboardingStateLoading,
		onboardingStateError,
		hasAudioBriefingChangeboardingBeenSeen,
		shouldSkip,
	]);

	// The effect that auto opens the popper
	useEffect(() => {
		// Always reset spotlight on content change or if conditions change
		setShowChangeboarding(false);

		if (shouldShowChangeboarding) {
			const timer = setTimeout(() => {
				setShowChangeboarding(true);
				onShow();
			}, ONBOARDING_DELAY_MS);
			return () => clearTimeout(timer);
		}
	}, [contentId, shouldShowChangeboarding, onShow]);

	const handleCloseChangeboarding = useCallback(() => {
		setOnboardingState({
			key: ONBOARDING_INSIGHTS.AUDIO_BRIEFING,
			value: 'true',
		});
		setShowChangeboarding(false);
	}, [setOnboardingState]);

	return { showChangeboarding, handleCloseChangeboarding };
};
